import { useState, useEffect } from 'react';
import { builderApi } from '../api';
import { fmtDate } from '../utils';

const TABS = ['Overall Stock', 'By Project'];

export default function BuilderInventory() {
  const [tab, setTab] = useState(0);

  return (
    <div className="bld-card">
      <h2 style={{ margin: '0 0 16px', fontSize: 18, color: 'var(--text-primary)' }}>Inventory</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '2px solid var(--border-color)', paddingBottom: 0 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '6px 18px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer',
            fontWeight: tab === i ? 700 : 400, fontSize: 14,
            background: tab === i ? 'var(--accent-blue, #3182ce)' : 'transparent',
            color: tab === i ? '#fff' : 'var(--text-secondary)',
            marginBottom: -2,
          }}>{t}</button>
        ))}
      </div>
      {tab === 0 && <OverallStockTab />}
      {tab === 1 && <ByProjectTab />}
    </div>
  );
}

function OverallStockTab() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    builderApi.getInventory().then(setInventory).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, []);

  const filtered = inventory.filter(i =>
    (i.material_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce((acc, m) => {
    const cat = m.category || 'Uncategorised';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(m);
    return acc;
  }, {});

  const qtyColor = qty => {
    const n = Number(qty);
    if (n <= 0) return '#e53e3e';
    if (n < 10) return '#d69e2e';
    return '#38a169';
  };

  const totalItems = inventory.length;
  const lowStock = inventory.filter(i => Number(i.quantity) > 0 && Number(i.quantity) < 10).length;
  const outOfStock = inventory.filter(i => Number(i.quantity) <= 0).length;

  return (
    <>
      {err && <div style={{ color: '#e53e3e', marginBottom: 12, fontSize: 13 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Total Items', value: totalItems, color: '#3182ce' },
          { label: 'Low Stock',   value: lowStock,   color: '#d69e2e' },
          { label: 'Out of Stock',value: outOfStock,  color: '#e53e3e' },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: 'var(--bg-secondary)', borderRadius: 8,
            padding: '12px 16px', borderLeft: `4px solid ${s.color}` }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
        <div style={{ flex: 2 }}>
          <input className="form-input" placeholder="Search material or category…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ height: '100%', boxSizing: 'border-box' }} />
        </div>
      </div>

      {loading ? <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div> : (
        filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#718096', padding: 40, fontSize: 14 }}>
            {search ? 'No results found' : 'No inventory yet. Record purchases to populate stock.'}
          </div>
        ) : (
          <table className="data-table" style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '45%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Material</th>
                <th>Unit</th>
                <th style={{ textAlign: 'right' }}>In Stock</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([cat, items]) => (
                <>
                  <tr key={`cat-${cat}`}>
                    <td colSpan={4} style={{
                      fontSize: 12, fontWeight: 700, color: '#4a5568',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      background: 'var(--bg-secondary)', padding: '8px 12px',
                      borderTop: '2px solid var(--border-color)',
                    }}>{cat}</td>
                  </tr>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td>{item.material_name}</td>
                      <td>{item.unit || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 700, color: qtyColor(item.quantity), fontSize: 15 }}>
                          {Number(item.quantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                        </span>
                        {Number(item.quantity) > 0 && Number(item.quantity) < 10 && (
                          <span style={{ marginLeft: 6, fontSize: 10, background: '#fefcbf', color: '#744210',
                            padding: '1px 5px', borderRadius: 8 }}>LOW</span>
                        )}
                        {Number(item.quantity) <= 0 && (
                          <span style={{ marginLeft: 6, fontSize: 10, background: '#fed7d7', color: '#c53030',
                            padding: '1px 5px', borderRadius: 8 }}>OUT</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: '#718096' }}>{fmtDate(item.last_updated)}</td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        )
      )}
    </>
  );
}

function ByProjectTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    builderApi.getInventoryByProject()
      .then(setRows)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const projects = [...new Map(rows.map(r => [r.project_id, { id: r.project_id, name: r.project_name, status: r.project_status }])).values()]
    .sort((a, b) => a.name.localeCompare(b.name));

  const filtered = rows.filter(r => {
    const matchProject = !selectedProject || String(r.project_id) === selectedProject;
    const matchSearch = !search ||
      (r.material_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.category || '').toLowerCase().includes(search.toLowerCase());
    return matchProject && matchSearch;
  });

  const grouped = filtered.reduce((acc, r) => {
    const key = r.project_id;
    if (!acc[key]) acc[key] = { name: r.project_name, status: r.project_status, items: [] };
    acc[key].items.push(r);
    return acc;
  }, {});

  const statusColor = s => ({ 'COMPLETED': '#38a169', 'IN_PROGRESS': '#3182ce', 'ON_HOLD': '#d69e2e' }[s] || '#718096');

  const fmtQty = n => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });

  return (
    <>
      {err && <div style={{ color: '#e53e3e', marginBottom: 12, fontSize: 13 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1.5 }}>
          <select className="form-input" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
            <option value="">All Projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 2 }}>
          <input className="form-input" placeholder="Search material or category…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign: 'center', color: '#718096', padding: 40, fontSize: 14 }}>
          {rows.length === 0
            ? 'No project-linked purchases yet. Assign a project when recording purchases.'
            : 'No results found.'}
        </div>
      ) : (
        Object.entries(grouped).map(([pid, proj]) => (
          <div key={pid} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
              padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8,
              borderLeft: `4px solid ${statusColor(proj.status)}` }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{proj.name}</span>
              {proj.status && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: statusColor(proj.status) + '22', color: statusColor(proj.status) }}>
                  {proj.status.replace('_', ' ')}
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#718096' }}>
                {proj.items.length} material{proj.items.length !== 1 ? 's' : ''}
              </span>
            </div>
            <table className="data-table" style={{ width: '100%', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '30%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Category</th>
                  <th>Unit</th>
                  <th style={{ textAlign: 'right' }}>Purchased</th>
                  <th style={{ textAlign: 'right' }}>Issued</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {proj.items.map((item, idx) => {
                  const purchased = Number(item.purchased_qty);
                  const issued = Number(item.issued_qty);
                  const balance = purchased - issued;
                  const balColor = balance <= 0 ? '#e53e3e' : balance < purchased * 0.2 ? '#d69e2e' : '#38a169';
                  return (
                    <tr key={idx}>
                      <td style={{ fontWeight: 500 }}>{item.material_name}</td>
                      <td style={{ fontSize: 12, color: '#718096' }}>{item.category || '—'}</td>
                      <td>{item.unit || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#3182ce' }}>
                        {fmtQty(purchased)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: issued > 0 ? '#d69e2e' : '#a0aec0' }}>
                        {fmtQty(issued)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 700, color: balColor }}>{fmtQty(balance)}</span>
                        {balance <= 0 && (
                          <span style={{ marginLeft: 5, fontSize: 10, background: '#fed7d7', color: '#c53030', padding: '1px 5px', borderRadius: 8 }}>OUT</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--bg-secondary)', fontWeight: 700 }}>
                  <td colSpan={3} style={{ padding: '6px 12px', fontSize: 12, color: '#4a5568' }}>
                    Project Total
                  </td>
                  <td style={{ textAlign: 'right', color: '#3182ce', padding: '6px 12px' }}>
                    {fmtQty(proj.items.reduce((s, i) => s + Number(i.purchased_qty), 0))}
                  </td>
                  <td style={{ textAlign: 'right', color: '#d69e2e', padding: '6px 12px' }}>
                    {fmtQty(proj.items.reduce((s, i) => s + Number(i.issued_qty), 0))}
                  </td>
                  <td style={{ textAlign: 'right', color: '#38a169', padding: '6px 12px' }}>
                    {fmtQty(proj.items.reduce((s, i) => s + Number(i.purchased_qty) - Number(i.issued_qty), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ))
      )}
    </>
  );
}
